import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bot, Check, X, Pencil, Loader2,
  GitMerge, EyeOff, Flag, Minus, ShieldCheck, RefreshCw, ChevronDown,
  Download, Layers, ArrowLeft, FileSpreadsheet, FileText, Code2,
} from 'lucide-react';
import { useReadOnly } from '../../context/ReadOnlyContext';
import useFusionPtrStore from '../../stores/useFusionPtrStore';
import { fusionPtrService } from '../../services/fusionPtrService';
import { formatPriority, computeFinalOutput, applyFinalMeasureOverrides, hydrateFinalMeasures } from './fusionFormat';
import FinalMeasureModal from './FinalMeasureModal';
import ResponsibleSelect from './ResponsibleSelect';

const typeMeta = {
  fuse: { icon: GitMerge, badge: 'bg-blue-100 text-blue-700', labelKey: 'fusionPtr.typeFuse' },
  cover: { icon: EyeOff, badge: 'bg-amber-100 text-amber-700', labelKey: 'fusionPtr.typeCover' },
  conflict: { icon: Flag, badge: 'bg-red-100 text-red-700', labelKey: 'fusionPtr.typeConflict' },
  keep: { icon: Minus, badge: 'bg-slate-100 text-slate-600', labelKey: 'fusionPtr.typeKeep' },
};

const statusBadge = {
  pending: 'bg-yellow-100 text-yellow-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-slate-200 text-slate-500',
  edited: 'bg-purple-100 text-purple-700',
};

// Left rail color per proposal type — decision status readable while scrolling.
const typeRail = {
  fuse: 'border-l-blue-500',
  cover: 'border-l-amber-500',
  conflict: 'border-l-red-500',
  keep: 'border-l-slate-300',
};

const typeFallback = { fuse: 'Fusion', cover: 'Couverture', conflict: 'Conflit', keep: 'Conservée' };

// Translated proposal status + AI confidence labels (raw values shown as fallback).
const statusLabelKeys = {
  pending: ['fusionPtr.statusPending', 'En attente'],
  accepted: ['fusionPtr.statusAccepted', 'Acceptée'],
  rejected: ['fusionPtr.statusRejected', 'Refusée'],
  edited: ['fusionPtr.statusEdited', 'Modifiée'],
};
const confidenceLabelKeys = {
  high: ['fusionPtr.confidenceHigh', 'Haute'],
  medium: ['fusionPtr.confidenceMedium', 'Moyenne'],
  low: ['fusionPtr.confidenceLow', 'Faible'],
};

// Human-in-the-loop validation of AI fusion proposals.
// Sources are never modified: accept/edit/reject only affects this fusion doc.
const FusionWorkspace = ({ fusion, onChanged, onRefresh }) => {
  const { t, i18n } = useTranslation();
  const { isViewer } = useReadOnly();
  const [proposals, setProposals] = useState(fusion.proposals || []);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [editingFinal, setEditingFinal] = useState(null); // { key, field } — PTR Final inline edit
  const [finalEditValue, setFinalEditValue] = useState('');
  const [finalModalKey, setFinalModalKey] = useState(null); // final measure key open in the full-form modal
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const autoRefreshInFlight = useRef(false);
  const autoRefreshRanFor = useRef(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    if (exportMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [exportMenuOpen]);

  useEffect(() => {
    setProposals(fusion.proposals || []);
    setEditingId(null);
  }, [fusion]);

  const measureByKey = useMemo(() => {
    const map = new Map();
    (fusion.snapshot || []).forEach((snap) => {
      (snap.measures || []).forEach((m) => {
        map.set(m.key, { ...m, analysisName: snap.analysisName });
      });
    });
    return map;
  }, [fusion]);

  // Union of the source analyses' participant lists (Atelier 1 context frozen
  // per snapshot) — same source as the Atelier 5 measures-table dropdown.
  const responsibleOptions = useMemo(() => {
    const seen = new Map();
    (fusion.snapshot || []).forEach((snap) => {
      (snap.responsiblePersonsList || []).forEach((p) => {
        const name = String(p?.name || '').trim();
        if (!name || seen.has(name)) return;
        seen.set(name, { name, role: p.role || '', analysisName: snap.analysisName || '' });
      });
    });
    return [...seen.values()];
  }, [fusion.snapshot]);

  // Stale sources: live analysis newer than the frozen snapshot entry.
  const analyses = useFusionPtrStore((s) => s.analyses);
  const staleNames = useMemo(() => {
    const frozenById = new Map(
      (fusion.sourceUpdatedAt || []).map((e) => [String(e.analysisId), e.updatedAt]),
    );
    const names = [];
    (fusion.sourceAnalysisIds || []).forEach((rawId) => {
      const id = String(rawId);
      const live = (analyses || []).find((a) => String(a._id || a.id) === id);
      const frozen = frozenById.get(id);
      if (live?.updatedAt && frozen && new Date(live.updatedAt) > new Date(frozen)) {
        names.push(live.name || id);
      }
    });
    return names;
  }, [analyses, fusion.sourceAnalysisIds, fusion.sourceUpdatedAt]);

  // Snapshots collected before participant lists were frozen lack the field —
  // backfill them once through the same safe refresh.
  const needsBackfill = useMemo(() => (
    (fusion.snapshot || []).some((s) => !Array.isArray(s.responsiblePersonsList))
  ), [fusion.snapshot]);

  // Auto-sync whenever sources moved (draft only, no manual button): the run
  // signature (fusion + staleness + focus) gates re-runs — a successful reload
  // clears the condition itself, so there is no loop; a failed/unchanged
  // reload keeps the same signature and does not refire. Skipped while the
  // user is mid-edit so the reload never wipes an open form (it retries once
  // editing ends). Refs reset on unmount, so every fresh open retries; window
  // focus re-evaluates (covers edits/backend restarts that happened elsewhere
  // while this workspace stayed open).
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => {
    const onFocus = () => setFocusTick((n) => n + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  const isEditingSomething = editingId !== null || editingFinal !== null || finalModalKey !== null;
  useEffect(() => {
    if ((!staleNames.length && !needsBackfill) || isViewer || fusion.status === 'validated' || !onRefresh) return;
    if (isEditingSomething || autoRefreshInFlight.current) return;
    const signature = `${fusion._id}|${staleNames.join(',')}|${needsBackfill ? '1' : '0'}|${focusTick}`;
    if (autoRefreshRanFor.current === signature) return;
    autoRefreshRanFor.current = signature;
    autoRefreshInFlight.current = true;
    setAutoRefreshing(true);
    Promise.resolve()
      .then(() => onRefresh())
      .then(() => setNotice(t('fusionPtr.refreshedNotice', 'Données sources actualisées.')))
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => { autoRefreshInFlight.current = false; setAutoRefreshing(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fusion._id, staleNames, needsBackfill, isEditingSomething, focusTick]);

  const decided = proposals.filter((p) => p.status !== 'pending').length;
  const frozen = fusion.status === 'validated';
  const [viewMode, setViewMode] = useState('proposals');

  const { finalMeasures: baseFinalMeasures } = useMemo(() => {
    if (frozen && fusion.finalMeasures?.length) {
      return {
        finalMeasures: fusion.finalMeasures,
        coverageMap: fusion.coverageMap || [],
      };
    }
    return computeFinalOutput(proposals, fusion.snapshot || []);
  }, [frozen, fusion.finalMeasures, fusion.coverageMap, fusion.snapshot, proposals]);

  // PTR Final inline edits (persisted as overrides, proposals untouched).
  // Local overrides apply instantly without reloading the whole fusion
  // (no toolbar flash, no scroll/focus loss); the server copy syncs underneath.
  const [localOverrides, setLocalOverrides] = useState({});
  useEffect(() => { setLocalOverrides({}); }, [fusion._id]);
  const mergedOverrides = useMemo(() => {
    const merged = { ...(fusion.finalMeasureOverrides || {}) };
    for (const [key, ov] of Object.entries(localOverrides)) {
      merged[key] = { ...(merged[key] || {}), ...ov };
    }
    return merged;
  }, [fusion.finalMeasureOverrides, localOverrides]);
  const finalMeasures = useMemo(() => {
    // Stored finals from older validations can be minimal — hydrate them
    // against the frozen snapshot exactly like the export does.
    const hydrated = frozen ? hydrateFinalMeasures(baseFinalMeasures, fusion.snapshot) : baseFinalMeasures;
    return applyFinalMeasureOverrides(hydrated, mergedOverrides);
  }, [baseFinalMeasures, frozen, fusion.snapshot, mergedOverrides]);
  const canEditFinal = !isViewer && !frozen;

  const nonFusedMeasures = useMemo(() => {
    return finalMeasures.filter((m) => m.origin === 'kept' || !m.origin);
  }, [finalMeasures]);

  const persist = async (next, extra = {}) => {
    setSaving(true);
    setError(null);
    try {
      await fusionPtrService.updateFusion(fusion._id, { proposals: next, ...extra });
      setProposals(next);
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (proposals.length && !window.confirm(t('fusionPtr.confirmRegenerate', 'Remplacer les propositions existantes par une nouvelle analyse IA ?'))) return;
    setGenerating(true);
    setError(null);
    setNotice(null);
    try {
      const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
      const response = await fusionPtrService.suggestFusion(fusion._id, lang);
      const fresh = response.data?.proposals || [];
      const dropped = Number(response.data?.droppedIncomplete) || 0;
      const notes = [];
      if (dropped > 0) {
        notes.push(t('fusionPtr.droppedNotice', '{{count}} proposition(s) écartée(s) — traçabilité incomplète.', { count: dropped }));
      }
      if (response.data?.largeContext) {
        notes.push(t('fusionPtr.largeContextNotice', 'Contexte très volumineux — vérifiez attentivement chaque proposition.'));
      }
      if (notes.length) setNotice(notes.join(' '));
      await persist(fresh);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const setStatus = (id, status) => {
    persist(proposals.map((p) => (p.id === id ? { ...p, status } : p)));
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    const fm = p.fusedMeasure || {};
    setDraft({
      ...fm,
      name: fm.name || '',
      description: fm.description || '',
      budget: fm.budget ?? 0,
      estimatedEffort: fm.estimatedEffort ?? 0,
      priority: formatPriority(fm.priority) || '',
      category: fm.category || '',
      responsiblePerson: fm.responsiblePerson || '',
      deadline: fm.deadline || '',
    });
  };

  // Final-row fields fed by the proposal editor — an older Final
  // override for the same row must not keep masking the edited values
  // (the final-table justification override, if any, is preserved).
  const PROPOSAL_SYNCED_FIELDS = ['name', 'description', 'category', 'priority', 'budget', 'estimatedEffort', 'responsiblePerson', 'deadline'];

  const dropProposalSyncedOverride = (overrides, finalKey) => {
    if (!overrides?.[finalKey]) return null;
    const entry = { ...overrides[finalKey] };
    PROPOSAL_SYNCED_FIELDS.forEach((f) => { delete entry[f]; });
    const next = { ...overrides };
    if (Object.keys(entry).length) next[finalKey] = entry;
    else delete next[finalKey];
    return next;
  };

  const saveEdit = (id) => {
    const nextProposals = proposals.map((p) => (p.id === id ? {
      ...p,
      fusedMeasure: {
        ...(p.fusedMeasure || {}),
        name: String(draft.name || ''),
        description: String(draft.description || ''),
        budget: Math.max(0, Number(draft.budget) || 0),
        estimatedEffort: Math.max(0, Number(draft.estimatedEffort) || 0),
        priority: String(draft.priority || ''),
        category: String(draft.category || ''),
        responsiblePerson: String(draft.responsiblePerson || ''),
        deadline: String(draft.deadline || ''),
      },
      status: 'edited',
    } : p));
    // The edited values flow into PTR Final via computeFinalOutput —
    // clear the same fields from this row's override so the final table
    // reflects the edit instead of the stale override.
    const finalKey = `fusion::${id}`;
    const cleaned = dropProposalSyncedOverride(
      { ...(fusion.finalMeasureOverrides || {}), ...localOverrides },
      finalKey,
    );
    setLocalOverrides((cur) => dropProposalSyncedOverride(cur, finalKey) || cur);
    persist(nextProposals, cleaned ? { finalMeasureOverrides: cleaned } : {});
    setEditingId(null);
  };

  const handleValidate = async () => {
    setSaving(true);
    setError(null);
    try {
      await fusionPtrService.updateFusion(fusion._id, { status: 'validated' });
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (format, lang = 'fr') => {
    setExporting(true);
    setExportMenuOpen(false);
    setError(null);
    try {
      if (format === 'pdf') {
        await fusionPtrService.exportPdf(fusion, finalMeasures, lang);
      } else {
        await fusionPtrService.exportFusion(fusion._id, format, lang);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  const handleReopen = async () => {
    setSaving(true);
    setError(null);
    try {
      await fusionPtrService.updateFusion(fusion._id, { status: 'draft' });
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // PTR Final inline edits — persisted as overrides (proposals untouched).
  // Applied locally first: no shared `saving` flash on toolbar buttons and no
  // full fusion reload (which visibly refreshed the whole workspace).
  const saveFinalEdit = async (key, field, value) => {
    const prev = { ...(fusion.finalMeasureOverrides || {}), ...localOverrides };
    const next = { ...prev, [key]: { ...(prev[key] || {}), [field]: value } };
    setError(null);
    try {
      await fusionPtrService.updateFusion(fusion._id, { finalMeasureOverrides: next });
      setLocalOverrides((cur) => ({ ...cur, [key]: { ...(cur[key] || {}), [field]: value } }));
    } catch (e) {
      setError(e.message);
    } finally {
      setEditingFinal(null);
      setFinalEditValue('');
    }
  };

  // Full-row save from the FinalMeasureModal — same override path.
  const saveFinalRow = async (key, values) => {
    const prev = { ...(fusion.finalMeasureOverrides || {}), ...localOverrides };
    const next = { ...prev, [key]: { ...(prev[key] || {}), ...values } };
    setError(null);
    try {
      await fusionPtrService.updateFusion(fusion._id, { finalMeasureOverrides: next });
      setLocalOverrides((cur) => ({ ...cur, [key]: { ...(cur[key] || {}), ...values } }));
    } catch (e) {
      setError(e.message);
    } finally {
      setFinalModalKey(null);
    }
  };

  const isFinalEditing = (key, field) => editingFinal && editingFinal.key === key && editingFinal.field === field;
  const startFinalEdit = (key, field, currentValue) => {
    if (!canEditFinal) return;
    setEditingFinal({ key, field });
    setFinalEditValue(currentValue ?? '');
  };

  const cancelFinalEdit = () => {
    setEditingFinal(null);
    setFinalEditValue('');
  };

  const commitFinalEdit = (key, field) => {
    if (editingFinal && editingFinal.key === key && editingFinal.field === field) {
      const value = finalEditValue;
      setEditingFinal(null);
      setFinalEditValue('');
      saveFinalEdit(key, field, value);
    }
  };

  const renderFinalEditable = (m, field, displayValue, type) => {
    if (isFinalEditing(m.key, field)) {
      return (
        <input
          autoFocus
          type={type || 'text'}
          value={finalEditValue}
          onChange={(e) => setFinalEditValue(e.target.value)}
          onBlur={() => commitFinalEdit(m.key, field)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitFinalEdit(m.key, field);
            if (e.key === 'Escape') cancelFinalEdit();
          }}
          className="w-full px-1.5 py-1 text-xs border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
      );
    }
    return (
      <span
        onClick={() => startFinalEdit(m.key, field, m[field] ?? '')}
        title={displayValue || t('fusionPtr.clickToEdit', 'Cliquer pour modifier')}
        className={canEditFinal
          ? 'cursor-pointer hover:bg-slate-100 px-1.5 py-1 rounded -mx-1.5 block min-h-[20px]'
          : 'block min-h-[20px]'}
      >
        {displayValue || '—'}
      </span>
    );
  };

  const renderSources = (p) => (
    <ul className="mt-1.5 space-y-1">
      {(p.sourceMeasureKeys || []).map((k, i) => {
        const m = measureByKey.get(k);
        const isCovering = p.type === 'cover' && i === p.sourceMeasureKeys.length - 1 && p.sourceMeasureKeys.length > 1;
        return (
          <li key={k} className="text-[11px] text-slate-600 flex items-start gap-1.5">
            <span className={`mt-0.5 px-1 rounded font-mono text-[10px] ${isCovering ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              {isCovering ? t('fusionPtr.covering', 'couvrante') : `#${i + 1}`}
            </span>
            <span>
              <span className="font-medium text-slate-700">{m?.name || k}</span>
              {m?.analysisName && <span className="text-slate-400"> — {m.analysisName}</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="space-y-4">
      {staleNames.length > 0 && !frozen && (
        <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
          <RefreshCw size={14} className={`flex-shrink-0 ${autoRefreshing ? 'animate-spin' : ''}`} />
          <span className="flex-1">
            {t('fusionPtr.stale', 'Données sources modifiées ({{names}}) — synchronisation automatique.', { names: staleNames.join(', ') })}
          </span>
        </div>
      )}
      {frozen && staleNames.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
          <RefreshCw size={14} className="flex-shrink-0" />
          <span className="flex-1">
            {t('fusionPtr.staleValidated', 'Données sources modifiées ({{names}}) — repassez en brouillon pour resynchroniser.', { names: staleNames.join(', ') })}
          </span>
        </div>
      )}
      {needsBackfill && !frozen && (
        <div className="flex items-center gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5">
          <RefreshCw size={14} className={`flex-shrink-0 ${autoRefreshing ? 'animate-spin' : ''}`} />
          <span className="flex-1">
            {t('fusionPtr.missingOwners', 'Listes de responsables manquantes — synchronisation automatique.')}
          </span>
        </div>
      )}
      <div data-testid="fusion-toolbar" className="sticky top-0 z-30 bg-white/95 backdrop-blur rounded-xl shadow-sm border border-slate-200">
        <div className="px-4 py-2.5 flex flex-wrap items-center justify-end gap-2">
              {!isViewer && !frozen && (
                <>
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={generating || saving}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generating ? <Loader2 size={16} className="animate-spin" /> : <Bot size={16} />}
                    {t('fusionPtr.generateFusion', 'Fusionner par l\u2019Assistant ACG')}
                  </button>
                  <button
                    type="button"
                    onClick={handleValidate}
                    disabled={!proposals.length || decided !== proposals.length || saving}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={decided !== proposals.length ? t('fusionPtr.validateHint', 'Acceptez, modifiez ou refusez chaque proposition avant de valider') : ''}
                  >
                    <ShieldCheck size={16} />
                    {t('fusionPtr.validate', 'Valider la fusion')}
                  </button>
                </>
              )}
              {!isViewer && frozen && (
                <button
                  type="button"
                  onClick={handleReopen}
                  disabled={saving}
                  title={t('fusionPtr.reopenHint', 'Repasser en brouillon pour modifier les mesures')}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Pencil size={16} />
                  {t('fusionPtr.reopen', 'Éditer')}
                </button>
              )}
              <div ref={exportMenuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setExportMenuOpen(!exportMenuOpen)}
                  disabled={exporting}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${exporting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  {t('fusionPtr.exportPtr', 'Exporter le PTR')}
                  <ChevronDown size={14} className={`transition-transform duration-200 ${exportMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {exportMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-slate-200 py-1.5 z-50 text-xs overflow-hidden">
                    <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5">
                      <FileText size={13} className="text-red-600" />
                      <span>{t('fusionPtr.exportSectionPdf', 'Rapport PDF')}</span>
                    </div>
                    <button
                      type="button"
                      className="w-full text-left px-3.5 py-2 text-slate-700 hover:bg-red-50 hover:text-red-800 transition-colors flex items-center justify-between group"
                      onClick={() => handleExport('pdf', 'fr')}
                    >
                      <span className="flex items-center gap-2">
                        <FileText size={14} className="text-red-600" />
                        <span className="font-medium">{t('fusionPtr.exportPdfFr', 'Français (.pdf)')}</span>
                      </span>
                      <span className="text-[10px] text-slate-400 group-hover:text-red-700 font-mono font-semibold">PDF</span>
                    </button>
                    <button
                      type="button"
                      className="w-full text-left px-3.5 py-2 text-slate-700 hover:bg-red-50 hover:text-red-800 transition-colors flex items-center justify-between group border-b border-slate-100"
                      onClick={() => handleExport('pdf', 'en')}
                    >
                      <span className="flex items-center gap-2">
                        <FileText size={14} className="text-red-600" />
                        <span className="font-medium">{t('fusionPtr.exportPdfEn', 'English (.pdf)')}</span>
                      </span>
                      <span className="text-[10px] text-slate-400 group-hover:text-red-700 font-mono font-semibold">PDF</span>
                    </button>

                    <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5">
                      <FileSpreadsheet size={13} className="text-emerald-600" />
                      <span>{t('fusionPtr.exportSectionExcel', 'Tableur Excel')}</span>
                    </div>
                    <button
                      type="button"
                      className="w-full text-left px-3.5 py-2 text-slate-700 hover:bg-emerald-50 hover:text-emerald-800 transition-colors flex items-center justify-between group"
                      onClick={() => handleExport('xlsx', 'fr')}
                    >
                      <span className="flex items-center gap-2">
                        <FileSpreadsheet size={14} className="text-emerald-600" />
                        <span className="font-medium">{t('fusionPtr.exportExcelFr', 'Français (.xlsx)')}</span>
                      </span>
                      <span className="text-[10px] text-slate-400 group-hover:text-emerald-700 font-mono font-semibold">XLSX</span>
                    </button>
                    <button
                      type="button"
                      className="w-full text-left px-3.5 py-2 text-slate-700 hover:bg-emerald-50 hover:text-emerald-800 transition-colors flex items-center justify-between group border-b border-slate-100"
                      onClick={() => handleExport('xlsx', 'en')}
                    >
                      <span className="flex items-center gap-2">
                        <FileSpreadsheet size={14} className="text-emerald-600" />
                        <span className="font-medium">{t('fusionPtr.exportExcelEn', 'English (.xlsx)')}</span>
                      </span>
                      <span className="text-[10px] text-slate-400 group-hover:text-emerald-700 font-mono font-semibold">XLSX</span>
                    </button>

                    <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5">
                      <Code2 size={13} className="text-purple-600" />
                      <span>{t('fusionPtr.exportSectionJson', 'Données brutes')}</span>
                    </div>
                    <button
                      type="button"
                      className="w-full text-left px-3.5 py-2 text-slate-700 hover:bg-purple-50 hover:text-purple-800 transition-colors flex items-center justify-between group"
                      onClick={() => handleExport('json')}
                    >
                      <span className="flex items-center gap-2">
                        <Code2 size={14} className="text-purple-600" />
                        <span>{t('fusionPtr.exportJson', 'JSON (complet)')}</span>
                      </span>
                      <span className="text-[10px] text-slate-400 group-hover:text-purple-700 font-mono font-semibold">JSON</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
        </div>
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 bg-slate-200/70 p-0.5 rounded-lg text-xs font-medium">
            <button
              type="button"
              onClick={() => setViewMode('proposals')}
              className={`px-3 py-1 rounded-md transition-colors ${viewMode === 'proposals' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
            >
              {t('fusionPtr.viewProposals', 'Propositions IA')} ({proposals.length})
            </button>
            <button
              type="button"
              onClick={() => setViewMode('final')}
              className={`px-3 py-1 rounded-md transition-colors ${viewMode === 'final' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
            >
              {t('fusionPtr.viewFinalPtr', 'PTR Final')} ({finalMeasures.length})
            </button>
          </div>
          <div className="flex items-center gap-3 min-w-[200px]">
            <div className="flex-1 h-1.5 bg-slate-200/70 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${proposals.length ? (decided / proposals.length) * 100 : 0}%` }}
              />
            </div>
            <span className="text-[11px] text-slate-500 tabular-nums whitespace-nowrap">{t('fusionPtr.decided', '{{done}} / {{total}} décidées', { done: decided, total: proposals.length })}</span>
          </div>
        </div>
        {(error || notice) && (
          <div className="px-6 py-3 space-y-2">
            {error && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
            )}
            {notice && (
              <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">{notice}</div>
            )}
          </div>
        )}

      {viewMode === 'final' ? (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <ShieldCheck size={16} className="text-green-600" />
                  {t('fusionPtr.viewFinalPtr', 'PTR Final')}
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  {t('fusionPtr.finalPtrSummary', 'PTR Final : {{total}} mesure(s) au total ({{fused}} fusionnée(s), {{kept}} conservée(s)) · Budget : {{budget}} € · Charge : {{effort}} j/h', {
                    total: finalMeasures.length,
                    fused: finalMeasures.filter((m) => m.origin === 'fused').length,
                    kept: finalMeasures.filter((m) => m.origin === 'kept' || !m.origin).length,
                    budget: finalMeasures.reduce((sum, m) => sum + (Number(m.budget) || 0), 0),
                    effort: finalMeasures.reduce((sum, m) => sum + (Number(m.estimatedEffort) || 0), 0),
                  })}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left text-slate-700 border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-600">
                    <th className="px-3 py-2.5">{t('fusionPtr.origin', 'Origine')}</th>
                    <th className="px-3 py-2.5">{t('fusionPtr.measure', 'Mesure')}</th>
                    <th className="px-3 py-2.5">{t('fusionPtr.source', 'Source(s)')}</th>
                    <th className="px-3 py-2.5">{t('fusionPtr.categoryLabel', 'Catégorie')}</th>
                    <th className="px-3 py-2.5">{t('fusionPtr.priority', 'Priorité')}</th>
                    <th className="px-3 py-2.5 text-right">{t('fusionPtr.budget', 'Budget (€)')}</th>
                    <th className="px-3 py-2.5 text-right">{t('fusionPtr.effortLabel', 'Charge (j/h)')}</th>
                    <th className="px-3 py-2.5">{t('fusionPtr.responsibleLabel', 'Responsable')}</th>
                    <th className="px-3 py-2.5">{t('fusionPtr.deadlineLabel', 'Échéance')}</th>
                    <th className="px-3 py-2.5 text-center w-12">{t('fusionPtr.actionsHeader', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {finalMeasures.map((m) => {
                    const originCls = m.origin === 'fused'
                      ? 'bg-blue-100 text-blue-700'
                      : m.origin === 'covering'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-slate-100 text-slate-600';
                    const originLabel = m.origin === 'fused'
                      ? t('fusionPtr.originFused', 'Fusionnée')
                      : m.origin === 'covering'
                      ? t('fusionPtr.originCovering', 'Couvrante')
                      : t('fusionPtr.originKept', 'Conservée');
                    return (
                      <tr key={m.key} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${originCls}`}>
                            {originLabel}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-800">
                            {renderFinalEditable(m, 'name', m.name || m.title || m.measureName)}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {renderFinalEditable(m, 'description', m.description)}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-[11px] text-slate-500">
                          {(m.sources || []).map((s) => s.analysisName || s.name).join(' + ') || '—'}
                        </td>
                        <td className="px-3 py-2">
                          {canEditFinal ? (
                            <select
                              value={m.category || 'other'}
                              onChange={(e) => saveFinalEdit(m.key, 'category', e.target.value)}
                              className="text-[11px] border border-slate-200 rounded px-1 py-0.5 pr-5 focus:outline-none focus:ring-1 focus:ring-blue-400 max-w-[110px]"
                            >
                              <option value="gouvernance et anticipation">{t('workshop5.category.governance', 'Gouvernance')}</option>
                              <option value="protection">{t('workshop5.category.protection', 'Protection')}</option>
                              <option value="défense">{t('workshop5.category.defense', 'Défense')}</option>
                              <option value="résilience">{t('workshop5.category.resilience', 'Résilience')}</option>
                              <option value="other">{t('workshop5.category.other', 'Autre')}</option>
                            </select>
                          ) : (
                            <span className="text-[11px] text-slate-600">{m.category || '—'}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {(() => {
                            const order = { P1: 4, P2: 3, P3: 2, P4: 1 };
                            const cur = order[formatPriority(m.priority)] || 0;
                            const words = ['', 'low', 'medium', 'high', 'critical'];
                            const labels = ['—', 'P4', 'P3', 'P2', 'P1'];
                            const colors = ['', 'bg-green-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-500'];
                            if (!canEditFinal) {
                              return m.priority ? (
                                <span className="inline-flex px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-medium text-[10px]">
                                  {formatPriority(m.priority)}
                                </span>
                              ) : '—';
                            }
                            return (
                              <div className="flex items-center gap-1">
                                <span className={`inline-flex items-center justify-center w-7 h-4 rounded text-[9px] font-bold text-white ${colors[cur] || 'bg-gray-200 text-gray-500'}`}>
                                  {labels[cur]}
                                </span>
                                <div className="flex flex-col items-center gap-0.5">
                                  <button type="button" onClick={() => saveFinalEdit(m.key, 'priority', words[Math.min(4, cur + 1)])} className="text-[7px] text-slate-500 hover:text-slate-800 leading-none p-0 h-2">▲</button>
                                  <button type="button" onClick={() => saveFinalEdit(m.key, 'priority', words[Math.max(0, cur - 1)])} className="text-[7px] text-slate-500 hover:text-slate-800 leading-none p-0 h-2">▼</button>
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap font-medium">
                          {renderFinalEditable(m, 'budget', m.budget ? `${m.budget} €` : '', 'number')}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                          {canEditFinal ? (
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-[11px] font-mono font-bold text-slate-700 min-w-[16px] text-center">{Number(m.estimatedEffort) || 0}</span>
                              <div className="flex flex-col items-center gap-0.5">
                                <button type="button" onClick={() => saveFinalEdit(m.key, 'estimatedEffort', (Number(m.estimatedEffort) || 0) + 1)} className="text-[7px] text-slate-500 hover:text-slate-800 leading-none p-0 h-2">▲</button>
                                <button type="button" onClick={() => saveFinalEdit(m.key, 'estimatedEffort', Math.max(0, (Number(m.estimatedEffort) || 0) - 1))} className="text-[7px] text-slate-500 hover:text-slate-800 leading-none p-0 h-2">▼</button>
                              </div>
                              <span className="text-[9px] text-slate-500">j/h</span>
                            </div>
                          ) : (
                            m.estimatedEffort ? `${m.estimatedEffort} j/h` : '—'
                          )}
                        </td>
                        <td className="px-3 py-2 text-[11px] text-slate-600 whitespace-nowrap">
                          {renderFinalEditable(m, 'responsiblePerson', m.responsiblePerson)}
                        </td>
                        <td className="px-3 py-2 text-[11px] text-slate-600 whitespace-nowrap font-mono">
                          {renderFinalEditable(m, 'deadline', m.deadline, 'date')}
                        </td>
                        <td className="px-3 py-2 text-center whitespace-nowrap">
                          {canEditFinal && (
                            <button
                              type="button"
                              onClick={() => setFinalModalKey(m.key)}
                              title={t('common.edit', 'Modifier')}
                              className="inline-flex items-center justify-center w-7 h-7 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            >
                              <Pencil size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <FinalMeasureModal
            isOpen={finalModalKey !== null}
            onClose={() => setFinalModalKey(null)}
            measure={finalMeasures.find((fm) => fm.key === finalModalKey) || null}
            onSave={(values) => saveFinalRow(finalModalKey, values)}
            responsibleOptions={responsibleOptions}
          />
        </div>
      ) : !proposals.length ? (
        <div className="bg-gradient-to-br from-violet-50/80 via-white to-blue-50/80 rounded-2xl border border-violet-200/70 p-8 shadow-xs text-center space-y-4">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-tr from-violet-600 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-violet-500/20">
            <Bot size={26} />
          </div>
          <div className="max-w-md mx-auto">
            <h4 className="text-base font-bold text-slate-800">
              {t('fusionPtr.emptyProposalsHeroTitle', 'Prêt pour la consolidation intelligente')}
            </h4>
            <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">
              {t('fusionPtr.emptyProposalsHeroDesc', "L'Assistant ACG compare vos {{count}} analyses sources pour détecter les redondances, fusions et couvertures de mesures.", { count: (fusion.sourceAnalysisIds || []).length })}
            </p>
          </div>
          <p className="text-[11px] text-slate-400">
            {t('fusionPtr.noProposals', "Aucune proposition pour le moment — lancez la fusion par l'Assistant ACG.")}
          </p>
          {!isViewer && !frozen && (
            <div>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? <Loader2 size={16} className="animate-spin" /> : <Bot size={16} />}
                {t('fusionPtr.generateFusion', 'Fusionner par l\u2019Assistant ACG')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {proposals.map((p) => {
            const meta = typeMeta[p.type] || typeMeta.keep;
            const Icon = meta.icon;
            const isEditing = editingId === p.id;
            return (
              <div key={p.id} data-testid={`proposal-${p.id}`} className={`bg-white rounded-xl shadow-sm border border-slate-200 border-l-4 p-4 ${typeRail[p.type] || typeRail.keep}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${meta.badge}`}>
                    <Icon size={12} /> {t(meta.labelKey, typeFallback[p.type] || p.type)}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${statusBadge[p.status] || statusBadge.pending}`}>
                    {(() => {
                      const entry = statusLabelKeys[p.status];
                      return entry ? t(entry[0], entry[1]) : (p.status || '');
                    })()}
                  </span>
                  {p.confidence && (
                    <span className="text-[10px] text-slate-400">
                      {t('fusionPtr.confidence', 'confiance')}: {(() => {
                        const entry = confidenceLabelKeys[String(p.confidence).toLowerCase()];
                        return entry ? t(entry[0], entry[1]) : p.confidence;
                      })()}
                    </span>
                  )}
                </div>

                {(p.type === 'fuse' || p.type === 'cover') ? (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 mb-2">
                        <Layers size={12} />
                        {t('fusionPtr.sourcesTitle', 'Mesures sources ({{count}})', { count: (p.sourceMeasureKeys || []).length })}
                      </div>
                      <ul className="space-y-2">
                        {(p.sourceMeasureKeys || []).map((k, i) => {
                          const m = measureByKey.get(k);
                          const isCovering = p.type === 'cover' && i === p.sourceMeasureKeys.length - 1 && p.sourceMeasureKeys.length > 1;
                          const mapping = (p.sourceMapping || []).find((e) => e.key === k);
                          return (
                            <li key={k} className={`rounded-md border p-2 ${isCovering ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}>
                              <div className="flex items-start gap-1.5 text-[11px]">
                                <span className={`mt-0.5 px-1 rounded font-mono text-[10px] flex-shrink-0 ${isCovering ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                  {isCovering ? t('fusionPtr.covering', 'couvrante') : `#${i + 1}`}
                                </span>
                                <span className="min-w-0">
                                  <span className="font-medium text-slate-700">{m?.name || k}</span>
                                  {m?.analysisName && <span className="text-slate-400"> — {m.analysisName}</span>}
                                </span>
                              </div>
                              {mapping?.coveredBy && (
                                <p className="mt-1 text-[11px] text-slate-500 italic border-l-2 border-blue-300 pl-1.5">“{mapping.coveredBy}”</p>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    <div className={`rounded-lg border p-3 ${p.type === 'cover' ? 'bg-green-50/60 border-green-200' : 'bg-blue-50/60 border-blue-200'}`}>
                      {p.type === 'fuse' ? (
                        <>
                          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-blue-700 mb-2">
                            <GitMerge size={12} />
                            {t('fusionPtr.fusedTitle', 'Mesure fusionnée')}
                          </div>
                          {isEditing ? (
                            <div className="space-y-2">
                              <div>
                                <label className="block text-[11px] font-medium text-slate-600 mb-0.5">{t('workshop5RS.measure', 'Mesure de sécurité')}</label>
                                <input
                                  type="text" value={draft.name || ''}
                                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                  className="w-full text-sm font-medium border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                  placeholder={t('fusionPtr.measureName', 'Nom de la mesure fusionnée')}
                                />
                              </div>
                              <div>
                                <label className="block text-[11px] font-medium text-slate-600 mb-0.5">{t('workshop5RS.description', 'Description')}</label>
                                <textarea
                                  value={draft.description || ''} rows={2}
                                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                  className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                  placeholder={t('fusionPtr.measureDesc', 'Description')}
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-[11px] font-medium text-slate-600 mb-0.5">{t('workshop5RS.measureCategory', 'Catégorie')}</label>
                                  <select
                                    value={draft.category || ''}
                                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                                    className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                  >
                                    <option value="">—</option>
                                    <option value="gouvernance et anticipation">{t('workshop5.category.governance', 'Gouvernance')}</option>
                                    <option value="protection">{t('workshop5.category.protection', 'Protection')}</option>
                                    <option value="défense">{t('workshop5.category.defense', 'Défense')}</option>
                                    <option value="résilience">{t('workshop5.category.resilience', 'Résilience')}</option>
                                    <option value="other">{t('workshop5.category.other', 'Autre')}</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-[11px] font-medium text-slate-600 mb-0.5">{t('workshop5RS.responsible', 'Responsable')}</label>
                                  <ResponsibleSelect
                                    value={draft.responsiblePerson || ''}
                                    onChange={(v) => setDraft({ ...draft, responsiblePerson: v })}
                                    options={responsibleOptions}
                                  />
                                </div>
                                <div>
                                  <label className="block text-[11px] font-medium text-slate-600 mb-0.5">{t('workshop5RS.budget', 'Budget (€)')}</label>
                                  <input type="number" min="0" value={draft.budget ?? ''} onChange={(e) => setDraft({ ...draft, budget: e.target.value })} placeholder={t('fusionPtr.budget', 'Budget')} className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                </div>
                                <div>
                                  <label className="block text-[11px] font-medium text-slate-600 mb-0.5">{t('workshop5RS.estimatedEffort', 'Charge estimée (j/h)')}</label>
                                  <input type="number" min="0" value={draft.estimatedEffort ?? ''} onChange={(e) => setDraft({ ...draft, estimatedEffort: e.target.value })} placeholder={t('fusionPtr.effortLabel', 'Charge')} className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                </div>
                                <div>
                                  <label className="block text-[11px] font-medium text-slate-600 mb-0.5">{t('workshop5RS.deadline', 'Échéance')}</label>
                                  <input type="date" value={draft.deadline || ''} onChange={(e) => setDraft({ ...draft, deadline: e.target.value })} className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                </div>
                                <div>
                                  <label className="block text-[11px] font-medium text-slate-600 mb-0.5">{t('workshop5RS.priority', 'Priorité')}</label>
                                  <select
                                    value={formatPriority(draft.priority)}
                                    onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
                                    className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                  >
                                    <option value="">{t('fusionPtr.prioritySelect', 'Priorité...')}</option>
                                    <option value="P1">P1 ({t('fusionPtr.p1Label', 'Critique')})</option>
                                    <option value="P2">P2 ({t('fusionPtr.p2Label', 'Haute')})</option>
                                    <option value="P3">P3 ({t('fusionPtr.p3Label', 'Moyenne')})</option>
                                    <option value="P4">P4 ({t('fusionPtr.p4Label', 'Faible')})</option>
                                  </select>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button type="button" onClick={() => saveEdit(p.id)} className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">{t('common.save', 'Enregistrer')}</button>
                                <button type="button" onClick={() => setEditingId(null)} className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-800">{t('common.cancel', 'Annuler')}</button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="text-sm font-semibold text-slate-800">{p.fusedMeasure?.name || '—'}</div>
                              {p.fusedMeasure?.description && <p className="text-xs text-slate-600 mt-0.5">{p.fusedMeasure.description}</p>}
                              <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {!!p.fusedMeasure?.budget && <span className="inline-flex px-1.5 py-0.5 rounded bg-white/80 border border-blue-100 text-[10px] font-medium text-slate-600 tabular-nums">{t('fusionPtr.budget', 'Budget')} : {p.fusedMeasure.budget} €</span>}
                                {!!p.fusedMeasure?.estimatedEffort && <span className="inline-flex px-1.5 py-0.5 rounded bg-white/80 border border-blue-100 text-[10px] font-medium text-slate-600 tabular-nums">{t('fusionPtr.effortLabel', 'Charge')} : {p.fusedMeasure.estimatedEffort} j/h</span>}
                                {p.fusedMeasure?.priority && <span className="inline-flex px-1.5 py-0.5 rounded bg-white/80 border border-blue-100 text-[10px] font-medium text-slate-600">{t('fusionPtr.priority', 'Priorité')} : {formatPriority(p.fusedMeasure.priority)}</span>}
                                {p.fusedMeasure?.responsiblePerson && <span className="inline-flex px-1.5 py-0.5 rounded bg-white/80 border border-blue-100 text-[10px] font-medium text-slate-600">{t('fusionPtr.responsibleLabel', 'Responsable')} : {p.fusedMeasure.responsiblePerson}</span>}
                                {p.fusedMeasure?.deadline && <span className="inline-flex px-1.5 py-0.5 rounded bg-white/80 border border-blue-100 text-[10px] font-medium text-slate-600">{t('fusionPtr.deadlineLabel', 'Échéance')} : {p.fusedMeasure.deadline}</span>}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-green-700 mb-2">
                            <EyeOff size={12} />
                            {t('fusionPtr.coveringTitle', 'Mesure couvrante')}
                          </div>
                          {(() => {
                            const coveringKey = (p.sourceMeasureKeys || [])[(p.sourceMeasureKeys || []).length - 1];
                            const covering = measureByKey.get(coveringKey);
                            if (!covering) return <p className="text-xs text-slate-400 italic">{coveringKey || '—'}</p>;
                            return (
                              <div>
                                <div className="text-sm font-semibold text-slate-800">{covering.name}</div>
                                {covering.description && <p className="text-xs text-slate-600 mt-0.5">{covering.description}</p>}
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                  {!!covering.budget && <span className="inline-flex px-1.5 py-0.5 rounded bg-white/80 border border-green-200 text-[10px] font-medium text-slate-600 tabular-nums">{t('fusionPtr.budget', 'Budget')} : {covering.budget} €</span>}
                                  {covering.priority && <span className="inline-flex px-1.5 py-0.5 rounded bg-white/80 border border-green-200 text-[10px] font-medium text-slate-600">{t('fusionPtr.priority', 'Priorité')} : {formatPriority(covering.priority)}</span>}
                                </div>
                                {covering.analysisName && <p className="mt-1 text-[11px] text-slate-400">— {covering.analysisName}</p>}
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  renderSources(p)
                )}

                {p.justification && (
                  <p className="mt-2 text-[11px] text-slate-500 italic border-l-2 border-slate-200 pl-2">{p.justification}</p>
                )}

                {!isViewer && !frozen && !isEditing && (
                  <div className="mt-2.5 flex gap-2">
                    <button type="button" onClick={() => setStatus(p.id, 'accepted')} disabled={p.status === 'accepted' || saving} className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded disabled:opacity-40">
                      <Check size={12} /> {t('fusionPtr.accept', 'Accepter')}
                    </button>
                    {p.type === 'fuse' && (
                      <button type="button" onClick={() => startEdit(p)} disabled={saving} className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded disabled:opacity-40">
                        <Pencil size={12} /> {t('common.edit', 'Modifier')}
                      </button>
                    )}
                    <button type="button" onClick={() => setStatus(p.id, 'rejected')} disabled={p.status === 'rejected' || saving} className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-40">
                      <X size={12} /> {t('fusionPtr.reject', 'Refuser')}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FusionWorkspace;
